import dayjs from "dayjs";
import _ from "lodash";

export const toMap = <T, K, V = T>(
    array: T[],
    keySelector: (item: T) => K,
    valueSelector: (item: T) => V = (item) => item as unknown as V
): Map<K, V> => array.reduce((acc, item) => acc.set(keySelector(item), valueSelector(item)), new Map<K, V>());

export const distinct = <T,>(array: T[]): T[] => array.filter((value, index, arr) => arr.indexOf(value) === index);
export const distinctBy = <T,>(array: T[], selector: (item: T) => any): T[] => array.filter((item, index, arr) => arr.findIndex(i => selector(i) === selector(item)) === index);
export const getMinMax = <T,>(array: T[], selector: (item: T) => any): { min: number, max: number } => {
    if (!array.length)
        return { min: 0, max: 0 };

    return array.reduce((result, item) => ({
        min: selector(item) < result.min ? selector(item) : result.min,
        max: selector(item) > result.max ? selector(item) : result.max,
    }), { min: selector(array[0]), max: selector(array[0]) });
};
export function getPercentile<T>(arr: T[], selector: (item: T) => number | null, percentile: number): T | undefined {
    if (!arr.length) return undefined;
    const sorted = _.sortBy(arr, selector);
    const index = Math.ceil(percentile * sorted.length) - 1;
    return sorted[index];
};
export const withinYears = (date: Date | null, years: number): boolean => {
    if (!date) return false;
    const threshold = new Date();
    threshold.setFullYear(threshold.getFullYear() - years);
    return date >= threshold;
};

export const formatNumber = (value: number) => value.toLocaleString([], { maximumFractionDigits: value < 1000 ? 2 : 0 });
export const formatPrice = (value: number) => `$${formatNumber(value)}`;
export const formatPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10)
        return phone;

    const countryCode = digits.slice(0, digits.length - 10);
    const number = `(${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
    return countryCode ? `${countryCode} ${number}` : number;
};
export const formatDate = (date: Date) => (date.getMonth() + 1) + "/" + date.getDate() + "/" + date.getFullYear();
export const formatDateTime = (date: Date) => date.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
export const formatDateForInput = (date: Date | undefined) => date ? dayjs(date).format("YYYY-MM-DD") : "";
export const parseDate = (dateString: Date | string) => dateString ? new Date(`${dateString}T00:00`) : null;
export const parseDateTime = (dateTimeString: string) => new Date(`${dateTimeString}Z`);
export const safeJsonParse = <T>(value: string): T | null => {
    if (!value) return null;
    try {
        return JSON.parse(value) as T;
    } catch {
        return null;
    }
}

export const formatFileSize = (bytes: number, decimals = 2) => {
    if (bytes === 0)
        return "0 bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["bytes", "kb", "mb", "gb", "tb", "pb", "eb", "zb", "yb"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const calculateVariance = (
    itemLandSize: number | null,
    itemBuildingSize: number | null,
    referenceLandSize: number | null,
    referenceBuildingSize: number | null
): number | null => {
    if (!itemLandSize || !referenceLandSize)
        return null;

    const landVar = Math.abs(itemLandSize - referenceLandSize) / referenceLandSize;

    if (!referenceBuildingSize)
        return landVar;

    if (!itemBuildingSize)
        return null;

    const buildVar = Math.abs(itemBuildingSize - referenceBuildingSize) / referenceBuildingSize;

    return (landVar + buildVar) / 2;
};

export const base64ToFile = (base64: string, filename: string): File => {
    const byteString = atob(base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString.charCodeAt(i);
    }
    
    return new File([bytes], filename, { type: "image/jpeg" });
}

export const imageFileExtensions = ["png", "jpg", "jpeg"];

export const isImageFile = (fileName: string) => imageFileExtensions.some(ext => fileName.toLowerCase().endsWith(`.${ext}`));

export const excelFileExtensions = ["xlsx", "xls"];

export const isExcelFile = (fileName: string) => excelFileExtensions.some(ext => fileName.toLowerCase().endsWith(`.${ext}`));

export const withHttps = (url: string) => /^https?:\/\//i.test(url) ? url : `https://${url}`;

export const wait = (ms: number): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        setTimeout(() => {
            resolve();
        }, ms);
    });
}